import { writable, get } from "svelte/store";
import { user, contract } from "../stores/index";
import { SessionRepository } from "../repositories/index";

// Keeps the session cookie in sync with this tab and one contract per browser.
// Design: README.md, "Session cookie (embedded content)".
//
// state: "unavailable" (no session, feature disabled or failed) | "syncing" |
//        "ready" | "stale"
// conflict: null | { reason: "contract" | "user" | "logout", contractId }
export function createSessionCookieSync({
  user,
  contract,
  repository,
  channelName = "gl-session",
}) {
  const state = writable("unavailable");
  const conflict = writable(null);
  const tabId = crypto.randomUUID();

  let started = false;
  let channel = null;
  let unsubscribers = [];
  let lastKey = null;
  // set while another tab is active with a different identity
  let staleReason = null;
  let foreignOwner = null;

  function identity() {
    const currentUser = get(user);

    return {
      userId: currentUser?.data?.id ?? null,
      contractId: get(contract)?.id ?? null,
      token: currentUser?.token?.token ?? null,
    };
  }

  function announce(message) {
    channel?.postMessage({ tabId, ...message });
  }

  function adoptsForeignOwner(own) {
    return (
      staleReason === "contract" &&
      own.userId === foreignOwner?.userId &&
      own.contractId === foreignOwner?.contractId
    );
  }

  async function sync({ force = false } = {}) {
    const own = identity();
    if (!own.token || !own.userId || !own.contractId) return;
    // A stale tab must not take over on its own (e.g. on token renewal), only
    // by an explicit decision or by switching to the active tab's contract.
    if (staleReason && !force && !adoptsForeignOwner(own)) return;

    const key = `${own.userId}|${own.contractId}|${own.token}`;
    if (!force && key === lastKey) return;
    lastKey = key;

    leaveStale();
    // first, so the other tabs pause before the cookie changes under them
    announce({ type: "owner", userId: own.userId, contractId: own.contractId });

    state.set("syncing");
    try {
      await repository.createSession();
      // blocked meanwhile, so this late write may have replaced the cookie of
      // the active tab: ask it to write its own again
      if (staleReason) return announce({ type: "resync" });
      if (key !== lastKey) return; // superseded by a newer sync
      state.set("ready");
    } catch (error) {
      if (key !== lastKey) return;
      console.warn(
        "Session cookie could not be created, embedded content is unavailable:",
        error?.response?.status ?? error,
      );
      state.set("unavailable");
    }
  }

  // The active tab owns the stored user and contract (reload, new tabs), so a
  // stale tab keeps its changes, e.g. a renewed token, in memory.
  function markStale(reason, owner) {
    staleReason = reason;
    foreignOwner = owner;
    lastKey = null;
    user.pauseStorage();
    contract.pauseStorage();
    state.set("stale");
    conflict.set({ reason, contractId: owner?.contractId ?? null });
  }

  function leaveStale() {
    if (!staleReason) return;

    staleReason = null;
    foreignOwner = null;
    user.resumeStorage();
    contract.resumeStorage();
    conflict.set(null);
  }

  function resync() {
    if (staleReason) return;

    void sync({ force: true }); // handles its own errors
  }

  function onMessage({ data }) {
    if (!data || data.tabId === tabId) return;
    const own = identity();
    if (!own.userId) return; // not logged in here, nothing to protect

    if (data.type === "cleared") return markStale("logout", null);
    if (data.type === "resync") return resync();
    if (data.type !== "owner") return;
    if (data.userId !== own.userId) return markStale("user", data);
    if (!own.contractId) return; // still choosing a contract
    if (data.contractId !== own.contractId) return markStale("contract", data);
    if (!staleReason) return;

    // same user and contract: the cookie is valid for this tab as well
    leaveStale();
    state.set("ready");
  }

  // "Keep my contract here": this tab becomes the active one and the others
  // are blocked. New tabs open with its contract, apps and policies.
  function reclaim() {
    const synced = sync({ force: true }); // leaves the stale state right away

    user.persist();
    contract.persist();

    return synced;
  }

  // A no-op unless started, so a disabled feature flag sends no requests.
  // Must run before the token is discarded, the request is authenticated by it.
  async function clear() {
    if (!started) return;
    lastKey = null;
    try {
      await repository.deleteSession();
    } catch (error) {
      // an expired token means the cookie (same Max-Age) has expired as well
      console.warn(
        "Session cookie could not be deleted:",
        error?.response?.status ?? error,
      );
    }
    // a logout ends the session in every tab, so it must reach the storage
    // even from a blocked tab
    leaveStale();
    announce({ type: "cleared" });
    state.set("unavailable");
  }

  function start() {
    if (started) return;
    started = true;
    if (typeof BroadcastChannel !== "undefined") {
      channel = new BroadcastChannel(channelName);
      channel.onmessage = onMessage;
    }
    const run = () => sync();
    unsubscribers = [user.subscribe(run), contract.subscribe(run)];
  }

  function stop() {
    unsubscribers.forEach((unsubscribe) => unsubscribe());
    unsubscribers = [];
    channel?.close();
    channel = null;
    started = false;
  }

  return {
    state: { subscribe: state.subscribe },
    conflict: { subscribe: conflict.subscribe },
    start,
    stop,
    reclaim,
    clear,
  };
}

const sessionCookie = createSessionCookieSync({
  user,
  contract,
  repository: SessionRepository,
});

export default sessionCookie;
